import { v4 as uuidv4 } from 'uuid';
import { MAX_PROFILES } from '@/constants/EditMaxes.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { LastConfigRemainingError } from '@/errors/edit/LastConfigRemainingError.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import { ConfigNotFoundError } from '@/errors/edit/ConfigNotFoundError.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { NameInUseError } from '@/errors/edit/NameInUseError.ts';
import { ValidationFailedError } from '@/errors/edit/ValidationFailedError.ts';
import { DefaultProfileConfigs } from '@/constants/DefaultProfileConfigs.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { InvalidInputError } from '@/errors/edit/InvalidInputError.ts';

export abstract class ProfileConfigService {
    public static createProfile(
        name: ProfileConfig['name'],
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
        newProfileConfigId: ProfileConfig['id'];
        newStepConfigId: StepConfig['id'];
        newPromptConfigId: PromptConfig['id'];
    } {
        const newProfileConfigId = uuidv4();

        if (Object.values(profileConfigs).length >= MAX_PROFILES) {
            throw new EditLimitReachedError('Max profiles reached');
        }

        if (
            Object.values(profileConfigs).find((p) => p.name.toLowerCase() === name.toLowerCase())
        ) {
            throw new InvalidInputError(`The profile name ${name} is already being used`);
        }

        profileConfigs = {
            ...profileConfigs,
            [newProfileConfigId]: {
                id: newProfileConfigId,
                name: name,
                promptIds: [],
                stepIds: [],
                editable: true,
            },
        };

        const {
            newStepConfigId,
            profileConfigs: stepUpdatedProfileConfigs,
            stepConfigs: stepUpdatedStepConfigs,
            promptConfigs: stepUpdatedPromptConfigs,
        } = StepConfigService.createStep(
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newProfileConfigId,
        );

        profileConfigs = stepUpdatedProfileConfigs;
        stepConfigs = stepUpdatedStepConfigs;
        promptConfigs = stepUpdatedPromptConfigs;

        const {
            newPromptConfigId,
            profileConfigs: promptUpdatedProfileConfigs,
            stepConfigs: promptUpdatedStepConfigs,
            promptConfigs: promptUpdatedPromptConfigs,
        } = PromptConfigService.createPrompt(
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newProfileConfigId,
            newStepConfigId,
        );

        profileConfigs = promptUpdatedProfileConfigs;
        stepConfigs = promptUpdatedStepConfigs;
        promptConfigs = promptUpdatedPromptConfigs;

        return {
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newProfileConfigId,
            newStepConfigId,
            newPromptConfigId,
        };
    }

    public static getAllProfileConfigs = (): Record<ProfileConfig['id'], ProfileConfig> => {
        const promptStore = usePromptStore();

        return {
            ...DefaultProfileConfigs,
            ...promptStore.profileConfigs,
        };
    };

    public static getCustomProfileConfigs = (): Record<ProfileConfig['id'], ProfileConfig> => {
        const promptStore = usePromptStore();

        return promptStore.profileConfigs;
    };

    public static getProfileConfig = (id: ProfileConfig['id']): ProfileConfig => {
        return this.getAllProfileConfigs()[id];
    };

    public static updateProfileName = (
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        profileConfigId: ProfileConfig['id'],
        name: ProfileConfig['name'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
    } => {
        const profileConfig = profileConfigs[profileConfigId];
        if (!profileConfig) {
            throw new ConfigNotFoundError(`Profile with id ${profileConfigId} not found`);
        }

        if (name.length === 0 || name.length > 50) {
            throw new ValidationFailedError(
                'Profile name must be between 1 and 50 characters long',
            );
        }

        if (
            Object.values(profileConfigs).find(
                (p) => p.name.toLowerCase() === name.toLowerCase() && p.id !== profileConfigId,
            )
        ) {
            throw new NameInUseError(`Profile name ${name} is already being used`);
        }

        profileConfigs = {
            ...profileConfigs,
            [profileConfigId]: {
                ...profileConfig,
                name: name,
            },
        };

        return {
            profileConfigs,
        };
    };

    public static duplicateProfile = (
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        stepConfigs: Record<StepConfig['id'], StepConfig>,
        promptConfigs: Record<PromptConfig['id'], PromptConfig>,
        profileConfigId: ProfileConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
        stepConfigs: Record<StepConfig['id'], StepConfig>;
        promptConfigs: Record<PromptConfig['id'], PromptConfig>;
        newProfileConfigId: ProfileConfig['id'];
    } => {
        const profileConfigToDuplicate = profileConfigs[profileConfigId];
        if (!profileConfigToDuplicate) {
            throw new ConfigNotFoundError(`Profile with id ${profileConfigId} not found`);
        }

        if (Object.values(profileConfigs).length >= MAX_PROFILES) {
            throw new EditLimitReachedError('Max profiles reached');
        }

        // Profile
        let newProfileName = `${profileConfigToDuplicate.name} (Copy)`;
        if (profileConfigToDuplicate.name.includes('(Copy)')) {
            newProfileName = profileConfigToDuplicate.name;
        }

        const newProfileConfigId = uuidv4();

        // Create new profile config
        profileConfigs = {
            ...profileConfigs,
            [newProfileConfigId]: {
                id: newProfileConfigId,
                name: newProfileName,
                promptIds: [],
                stepIds: [],
                editable: true,
            },
        };

        // Steps
        const profileStepConfigsToDuplicate = Object.values(stepConfigs).filter(
            (p) => p.profileId === profileConfigToDuplicate.id,
        );

        for (const stepConfigToDuplicate of profileStepConfigsToDuplicate) {
            const newStepConfigId = uuidv4();

            // Create new step config
            stepConfigs = {
                ...stepConfigs,
                [newStepConfigId]: {
                    ...stepConfigToDuplicate,
                    id: newStepConfigId,
                    promptIds: [],
                    profileId: newProfileConfigId,
                    editable: true,
                },
            };

            // Prompts
            const stepPromptConfigsToDuplicate = Object.values(promptConfigs).filter(
                (p) =>
                    p.profileId === profileConfigToDuplicate.id &&
                    p.stepId === stepConfigToDuplicate.id,
            );
            for (const promptConfigToDuplicate of stepPromptConfigsToDuplicate) {
                const newPromptConfigId = uuidv4();

                // Create new prompt config
                promptConfigs = {
                    ...promptConfigs,
                    [newPromptConfigId]: {
                        ...promptConfigToDuplicate,
                        id: newPromptConfigId,
                        profileId: newProfileConfigId,
                        stepId: newStepConfigId,
                        editable: true,
                    },
                };

                // Add prompt to new step config
                stepConfigs = {
                    ...stepConfigs,
                    [newStepConfigId]: {
                        ...stepConfigs[newStepConfigId],
                        promptIds: [...stepConfigs[newStepConfigId].promptIds, newPromptConfigId],
                    },
                };

                // Add prompt to new profile config
                profileConfigs = {
                    ...profileConfigs,
                    [newProfileConfigId]: {
                        ...profileConfigs[newProfileConfigId],
                        promptIds: [
                            ...profileConfigs[newProfileConfigId].promptIds,
                            newPromptConfigId,
                        ],
                    },
                };
            }

            // Add step to new profile config
            profileConfigs = {
                ...profileConfigs,
                [newProfileConfigId]: {
                    ...profileConfigs[newProfileConfigId],
                    stepIds: [...profileConfigs[newProfileConfigId].stepIds, newStepConfigId],
                },
            };
        }

        return {
            profileConfigs: profileConfigs,
            stepConfigs: stepConfigs,
            promptConfigs: promptConfigs,
            newProfileConfigId: newProfileConfigId,
        };
    };

    public static updateProfileConfigs(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
    ): void {
        const filteredConfigs = Object.fromEntries(
            Object.entries(profileConfigs).filter(([, config]) => config.editable),
        );
        const promptStore = usePromptStore();
        promptStore.setProfileConfigs(filteredConfigs);
    }

    public static deleteProfile(
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>,
        profileConfigId: ProfileConfig['id'],
    ): {
        profileConfigs: Record<ProfileConfig['id'], ProfileConfig>;
    } {
        if (Object.values(profileConfigs).length <= 1) {
            throw new LastConfigRemainingError('Cannot delete last profile');
        }

        const { [profileConfigId]: _, ...updatedProfileConfigs } = profileConfigs;

        return {
            profileConfigs: updatedProfileConfigs,
        };
    }

    public static validate(profileConfigs: Record<ProfileConfig['id'], ProfileConfig>): string[] {
        const errors: string[] = [];

        if (Object.values(profileConfigs).length === 0) {
            errors.push('There should at least be one profile');
        }

        for (const profileConfig of Object.values(profileConfigs)) {
            // Name
            if (profileConfig.name.length === 0) {
                errors.push('Profile name cannot be empty');
            } else if (profileConfig.name.length > 50) {
                errors.push('Profile name cannot be longer than 50 characters');
            }
        }

        return errors;
    }
}
